import React from 'react';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';

const MAP_LINES = [
    'M -40 170 C 150 90, 300 86, 476 138 S 830 256, 1020 210 S 1400 88, 1680 156',
    'M -30 318 C 164 248, 356 248, 538 292 S 888 372, 1108 324 S 1444 246, 1670 294',
    'M -60 498 C 148 430, 328 412, 518 454 S 882 574, 1096 528 S 1460 424, 1680 496',
    'M -40 676 C 186 598, 376 590, 560 636 S 882 750, 1080 704 S 1440 610, 1680 682',
    'M -20 842 C 194 768, 390 758, 596 810 S 936 920, 1174 876 S 1488 806, 1668 840',
    'M 148 0 C 132 190, 160 348, 208 534 S 266 886, 214 1040',
    'M 380 -20 C 346 188, 340 362, 390 552 S 460 888, 414 1048',
    'M 676 -20 C 656 170, 682 372, 734 564 S 798 888, 744 1040',
    'M 970 -20 C 934 196, 966 400, 1018 586 S 1080 884, 1032 1040',
    'M 1262 -10 C 1228 192, 1260 396, 1322 572 S 1386 888, 1338 1044',
    'M 72 104 L 238 144 L 312 264 L 244 382 L 92 420',
    'M 1030 122 L 1170 162 L 1244 292 L 1166 414 L 1018 454',
    'M 118 560 L 264 616 L 350 756 L 264 880 L 112 826',
    'M 1114 548 L 1272 590 L 1360 742 L 1266 878 L 1098 816',
];

const ROUTE_PATHS = [
    'M -60 760 C 168 668, 316 570, 506 506 S 900 398, 1140 308 S 1450 202, 1690 148',
    'M 138 944 C 240 776, 356 628, 540 512 S 826 330, 940 176 S 1116 -54, 1286 -128',
    'M 1160 944 C 1106 784, 1024 654, 900 564 S 644 386, 470 282 S 176 114, -30 78',
];

const STATIC_NODES = [
    [198, 694], [322, 612], [454, 540], [602, 476], [762, 432],
    [946, 366], [1128, 312], [1294, 258], [1466, 206],
    [236, 796], [352, 642], [512, 512], [690, 366], [826, 222],
    [1186, 778], [1014, 634], [858, 542], [706, 448], [530, 338],
];

const HIGHLIGHT_NODES = [
    [512, 512], [762, 432], [946, 366], [690, 366], [826, 222], [1014, 634],
];

const PULSE_NODES = [
    [454, 540], [762, 432], [946, 366], [1014, 634],
];

const RouteMapBackground = ({ compact = false, reduceMotion = false }) => {
    const theme = useTheme();
    const navy = '#102749';
    const routeBlue = theme.palette.primary.main;
    const routeCyan = '#56cbff';

    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                opacity: compact ? 0.78 : 1,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `
                        radial-gradient(circle at 20% 18%, ${alpha(routeCyan, 0.18)} 0, transparent 26%),
                        radial-gradient(circle at 78% 26%, ${alpha(routeBlue, 0.12)} 0, transparent 24%),
                        radial-gradient(circle at 64% 72%, ${alpha(routeCyan, 0.14)} 0, transparent 30%),
                        linear-gradient(135deg, #f8fbff 0%, #edf3fa 52%, #f7fbff 100%)
                    `,
                }}
            />
            <Box
                component="svg"
                viewBox="0 0 1600 1000"
                preserveAspectRatio="xMidYMid slice"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                }}
            >
                <defs>
                    <linearGradient id="routeBlue" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={alpha(routeBlue, 0.15)} />
                        <stop offset="50%" stopColor={alpha(routeCyan, 0.72)} />
                        <stop offset="100%" stopColor={alpha(routeBlue, 0.2)} />
                    </linearGradient>
                    <linearGradient id="routeBlueSecondary" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={alpha(routeBlue, 0.1)} />
                        <stop offset="50%" stopColor={alpha(routeBlue, 0.42)} />
                        <stop offset="100%" stopColor={alpha(routeCyan, 0.2)} />
                    </linearGradient>
                    <radialGradient id="heroWash" cx="34%" cy="34%" r="58%">
                        <stop offset="0%" stopColor={alpha('#ffffff', 0.92)} />
                        <stop offset="65%" stopColor={alpha('#ffffff', 0.4)} />
                        <stop offset="100%" stopColor={alpha('#ffffff', 0)} />
                    </radialGradient>
                    <radialGradient id="cardWash" cx="82%" cy="42%" r="42%">
                        <stop offset="0%" stopColor={alpha('#ffffff', 0.88)} />
                        <stop offset="100%" stopColor={alpha('#ffffff', 0)} />
                    </radialGradient>
                </defs>

                <rect x="0" y="0" width="1600" height="1000" fill="url(#heroWash)" />
                <rect x="0" y="0" width="1600" height="1000" fill="url(#cardWash)" />

                <g fill="none" stroke={alpha(navy, 0.06)} strokeWidth="2">
                    {MAP_LINES.map((line) => (
                        <path key={line} d={line} />
                    ))}
                </g>

                <g fill="none" stroke={alpha(navy, 0.045)} strokeWidth="1.2">
                    {STATIC_NODES.map(([cx, cy], index) => (
                        <circle key={`${cx}-${cy}-${index}`} cx={cx} cy={cy} r="20" />
                    ))}
                </g>

                <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d={ROUTE_PATHS[0]} stroke="url(#routeBlue)" strokeWidth="7" />
                    <path d={ROUTE_PATHS[0]} stroke={alpha(routeCyan, 0.35)} strokeWidth="2" strokeDasharray="10 12" />
                    <path d={ROUTE_PATHS[1]} stroke="url(#routeBlueSecondary)" strokeWidth="5" />
                    <path d={ROUTE_PATHS[1]} stroke={alpha(routeBlue, 0.24)} strokeWidth="1.5" strokeDasharray="6 10" />
                    <path d={ROUTE_PATHS[2]} stroke={alpha(routeBlue, 0.22)} strokeWidth="4" />
                    <path d={ROUTE_PATHS[2]} stroke={alpha(routeCyan, 0.16)} strokeWidth="1.2" strokeDasharray="5 12" />
                </g>

                <g>
                    {STATIC_NODES.map(([cx, cy], index) => (
                        <circle
                            key={`static-${cx}-${cy}-${index}`}
                            cx={cx}
                            cy={cy}
                            r="5"
                            fill={alpha(navy, 0.28)}
                        />
                    ))}
                    {HIGHLIGHT_NODES.map(([cx, cy], index) => (
                        <g key={`highlight-${cx}-${cy}-${index}`}>
                            <circle cx={cx} cy={cy} r="16" fill={alpha(routeCyan, 0.16)} />
                            <circle cx={cx} cy={cy} r="7" fill={alpha(routeCyan, 0.78)} />
                            <circle cx={cx} cy={cy} r="3" fill={alpha('#ffffff', 0.92)} />
                        </g>
                    ))}
                </g>

                {!reduceMotion && (
                    <g>
                        {PULSE_NODES.map(([cx, cy], index) => (
                            <circle
                                key={`pulse-${cx}-${cy}-${index}`}
                                cx={cx}
                                cy={cy}
                                r="7"
                                fill="none"
                                stroke={alpha(routeCyan, 0.72)}
                                strokeWidth="2"
                            >
                                <animate
                                    attributeName="r"
                                    values="7;21;7"
                                    dur={`${8 + index * 1.2}s`}
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="opacity"
                                    values="0.85;0.12;0.85"
                                    dur={`${8 + index * 1.2}s`}
                                    repeatCount="indefinite"
                                />
                            </circle>
                        ))}

                        <circle r="8" fill={alpha(routeCyan, 0.88)}>
                            <animateMotion dur="22s" repeatCount="indefinite" path={ROUTE_PATHS[0]} />
                            <animate attributeName="opacity" values="0;0.9;0.9;0" dur="22s" repeatCount="indefinite" />
                        </circle>
                        <circle r="6" fill={alpha(routeBlue, 0.84)}>
                            <animateMotion dur="18s" repeatCount="indefinite" path={ROUTE_PATHS[1]} />
                            <animate attributeName="opacity" values="0;0.88;0.88;0" dur="18s" repeatCount="indefinite" />
                        </circle>
                    </g>
                )}
            </Box>
        </Box>
    );
};

export default RouteMapBackground;
